import { useMemo } from "react";
import {
  createRouter,
  RouterProvider,
  createRoute,
  createRootRoute,
} from "@tanstack/react-router";
import { useGlobalContext } from "../../../utils/GlobalContext.ts";
import { StandardPageProviders } from "../../pages/StandardPageProviders/StandardPageProviders.tsx";
import { ForwardToLocaleRoute } from "../../pages/ForwardToLocaleRoute/ForwardToLocaleRoute.tsx";
import { NotFound } from "../../pages/NotFound/NotFound.tsx";

export const Router = () => {
  // Use context directly to avoid throwing error if not available
  const globalContext = useGlobalContext();

  const { localeConfig } = globalContext || { localeConfig: { locales: [] } };

  const router = useMemo(
    () => initRouter(localeConfig.locales),
    [localeConfig.locales],
  );

  // router.subscribe("onResolved", trackPageView);

  // If no global context, this should not happen as we handle it in main.tsx
  // But adding safety check just in case
  if (!globalContext) {
    console.warn("Router: GlobalContext not available");
    return null;
  }

  return <RouterProvider router={router} defaultNotFoundComponent={NotFound} />;
};

const initRouter = (supportedLocales: ReadonlyArray<string>) => {
  // Create root route
  const rootRoute = createRootRoute();

  // Create locale-specific routes
  const localeRoutes = supportedLocales
    .map((l) => l.toLowerCase())
    .map((localeCode) => {
      // Create locale route
      const localeRoute = createRoute({
        getParentRoute: () => rootRoute,
        path: `/${localeCode.toLowerCase()}`,
      });

      // Create index route for locale
      const indexRoute = createRoute({
        getParentRoute: () => localeRoute,
        path: "/",
        component: StandardPageProviders,
      });

      // Create dynamic slug route for locale
      const slugRoute = createRoute({
        getParentRoute: () => localeRoute,
        path: "/$slug",
        component: StandardPageProviders,
      });

      // Create 404 route for locale
      const notFoundRoute = createRoute({
        getParentRoute: () => localeRoute,
        path: "*",
        component: NotFound,
      });

      return { localeRoute, indexRoute, slugRoute, notFoundRoute };
    });

  // Create root index route for locale detection
  const rootIndexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/",
    component: ForwardToLocaleRoute,
  });

  // Create fallback route for any unmatched paths
  const fallbackRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "*",
    component: ForwardToLocaleRoute,
  });

  // Flatten all routes
  const allRoutes = [
    rootIndexRoute, // Add root index route first
    ...localeRoutes.flatMap(
      ({ localeRoute, indexRoute, slugRoute, notFoundRoute }) => [
        localeRoute,
        indexRoute,
        slugRoute,
        notFoundRoute,
      ],
    ),
    fallbackRoute,
  ];

  const routeTree = rootRoute.addChildren(allRoutes);
  return createRouter({ routeTree });
};
